import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FaPlus,
  FaSave,
  FaTrash,
  FaEdit,
  FaSync,
  FaFileImport,
  FaFileDownload,
  FaCheckCircle,
  FaExclamationTriangle,
  FaUniversity,
  FaMoneyCheckAlt,
  FaKeyboard,
  FaTags,
  FaSitemap,
  FaChartPie,
  FaTimes,
} from 'react-icons/fa';
import FinanceService from '../services/FinanceService';
import { useAuth } from '../AuthContext';
import {
  FINANCE_MANAGE_PERMISSION,
  FINANCE_VIEW_PERMISSION,
  hasPermission,
} from '../utils/permissions';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import ExpensesManager from '../components/ExpensesManager';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const DEFAULT_CATEGORY_COLORS = [
  '#0ea5e9',
  '#10b981',
  '#f97316',
  '#6366f1',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#14b8a6',
  '#ec4899',
  '#3b82f6',
];

const getColorByIndex = (preferredColor, index) => {
  if (typeof preferredColor === 'string' && preferredColor.trim()) {
    return preferredColor.trim();
  }
  return DEFAULT_CATEGORY_COLORS[index % DEFAULT_CATEGORY_COLORS.length];
};

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todas' },
  { value: 'PENDING', label: 'Pendentes' },
  { value: 'RECONCILED', label: 'Conciliadas' },
  { value: 'DIVERGENT', label: 'Divergentes' },
];

const INTEGRATION_TYPES = [
  { value: 'NONE', label: 'Lançamentos manuais (sem integração)' },
  { value: 'OFX', label: 'Importação via OFX' },
  { value: 'API', label: 'Integração bancária automática' },
];

const MANUAL_TRANSACTION_TYPES = [
  { value: 'CREDIT', label: 'Crédito' },
  { value: 'DEBIT', label: 'Débito' },
];

const emptyManualTransactionForm = {
  type: 'DEBIT',
  amount: '',
  occurredAt: '',
  description: '',
  notes: '',
  categoryId: '',
  subcategoryId: '',
};

const formatLocalDateTimeInput = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }
  const pad = (value) => String(value).padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const parseAmountInput = (rawValue) => {
  if (typeof rawValue === 'number' && Number.isFinite(rawValue)) {
    return rawValue;
  }
  if (rawValue === null || rawValue === undefined) {
    return Number.NaN;
  }
  const trimmed = String(rawValue).trim();
  if (!trimmed) {
    return Number.NaN;
  }
  const normalizedWhitespace = trimmed.replace(/\s+/g, '');
  const hasComma = normalizedWhitespace.includes(',');
  const hasDot = normalizedWhitespace.includes('.');
  let normalized = normalizedWhitespace;
  if (hasComma && hasDot) {
    normalized = normalized.replace(/\./g, '').replace(/,/g, '.');
  } else if (hasComma) {
    normalized = normalized.replace(/,/g, '.');
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
};

const emptyForm = {
  id: null,
  name: '',
  bankName: '',
  agencyNumber: '',
  accountNumber: '',
  description: '',
  integrationType: 'NONE',
  integrationConfig: '',
};

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(amount);
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const resolveErrorMessage = (error) => {
  if (!error) {
    return 'Ocorreu um erro inesperado.';
  }
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  if (typeof error.message === 'string') {
    return error.message;
  }
  return 'Ocorreu um erro inesperado.';
};

const toFormState = (account) => {
  if (!account) {
    return { ...emptyForm };
  }
  return {
    id: account.id ?? null,
    name: account.name ?? '',
    bankName: account.bankName ?? '',
    agencyNumber: account.agencyNumber ?? '',
    accountNumber: account.accountNumber ?? '',
    description: account.description ?? '',
    integrationType: account.integrationType ?? 'NONE',
    integrationConfig: account.integrationConfig
      ? JSON.stringify(account.integrationConfig, null, 2)
      : '',
  };
};

const parseIntegrationConfigText = (text) => {
  if (!text || !text.trim()) {
    return null;
  }
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed;
    }
    throw new Error();
  } catch (error) {
    throw new Error('A configuração da integração deve ser um JSON válido.');
  }
};

const STATUS_STYLES = {
  PENDING: 'bg-amber-100 text-amber-700',
  RECONCILED: 'bg-emerald-100 text-emerald-700',
  DIVERGENT: 'bg-rose-100 text-rose-700',
  CLEARED: 'bg-slate-100 text-slate-600',
};

const SOURCE_LABELS = {
  OFX: 'OFX',
  INTEGRATION: 'Integração',
  MANUAL: 'Manual',
};

const sortAccountsByName = (accounts) =>
  [...accounts].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }));

const buildDateParam = (value, endOfDay = false) => {
  if (!value) return undefined;
  const suffix = endOfDay ? 'T23:59:59' : 'T00:00:00';
  const date = new Date(`${value}${suffix}`);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  return date.toISOString();
};

export default function Finance() {
  const { user } = useAuth();
  const isDarkTheme =
    typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';
  const canView =
    hasPermission(user, FINANCE_VIEW_PERMISSION) || hasPermission(user, FINANCE_MANAGE_PERMISSION);
  const canManage = hasPermission(user, FINANCE_MANAGE_PERMISSION);

  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountsError, setAccountsError] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formState, setFormState] = useState({ ...emptyForm });
  const [formError, setFormError] = useState('');
  const [savingAccount, setSavingAccount] = useState(false);
  const [deletingAccountId, setDeletingAccountId] = useState(null);

  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionsError, setTransactionsError] = useState('');
  const [transactionSummary, setTransactionSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [selectedTransactionIds, setSelectedTransactionIds] = useState([]);
  const [markOthersAsDivergent, setMarkOthersAsDivergent] = useState(false);
  const [reconciliationNotes, setReconciliationNotes] = useState('');
  const [reconciling, setReconciling] = useState(false);

  const [importingOfx, setImportingOfx] = useState(false);
  const [syncingAccount, setSyncingAccount] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);

  const [showManualTransactionForm, setShowManualTransactionForm] = useState(false);
  const [manualFormState, setManualFormState] = useState(() => ({
    ...emptyManualTransactionForm,
    occurredAt: formatLocalDateTimeInput(new Date()),
  }));
  const [manualFormError, setManualFormError] = useState('');
  const [creatingManualTransaction, setCreatingManualTransaction] = useState(false);
  const [deletingTransactionIds, setDeletingTransactionIds] = useState([]);

  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoryError, setCategoryError] = useState('');
  const [subcategoryError, setSubcategoryError] = useState('');
  const [categoryFormState, setCategoryFormState] = useState({
    name: '',
    color: '',
    description: '',
  });
  const [subcategoryFormState, setSubcategoryFormState] = useState({
    categoryId: '',
    name: '',
    color: '',
    description: '',
  });
  const [categorySubmitting, setCategorySubmitting] = useState(false);
  const [subcategorySubmitting, setSubcategorySubmitting] = useState(false);
  const [categoryDeletingId, setCategoryDeletingId] = useState(null);
  const [subcategoryDeletingId, setSubcategoryDeletingId] = useState(null);
  const [categoryUpdatingId, setCategoryUpdatingId] = useState(null);
  const [subcategoryUpdatingId, setSubcategoryUpdatingId] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [subcategoryFilter, setSubcategoryFilter] = useState('');
  const [showCategoryReport, setShowCategoryReport] = useState(false);
  const [categoryReportData, setCategoryReportData] = useState(null);
  const [categoryReportLoading, setCategoryReportLoading] = useState(false);
  const [categoryReportError, setCategoryReportError] = useState('');

  const [showCategoryManager, setShowCategoryManager] = useState(false);

  const [editingTransactionId, setEditingTransactionId] = useState(null);
  const [editingTransactionDescription, setEditingTransactionDescription] = useState('');
  const [editingTransactionError, setEditingTransactionError] = useState('');
  const [updatingTransaction, setUpdatingTransaction] = useState(false);
  const [editingTransactionCategoryId, setEditingTransactionCategoryId] = useState('');
  const [editingTransactionSubcategoryId, setEditingTransactionSubcategoryId] = useState('');

  const [alert, setAlert] = useState(null);

  const fileInputRef = useRef(null);

  const selectedAccount = useMemo(
    () => accounts.find((account) => account.id === selectedAccountId) || null,
    [accounts, selectedAccountId],
  );

  const categoriesMap = useMemo(() => {
    const map = new Map();
    categories.forEach((category) => {
      if (category && typeof category.id === 'number') {
        map.set(category.id, category);
      }
    });
    return map;
  }, [categories]);

  const subcategoriesByCategory = useMemo(() => {
    const map = new Map();
    categories.forEach((category) => {
      if (category && typeof category.id === 'number') {
        const list = Array.isArray(category.subcategories)
          ? category.subcategories
          : [];
        map.set(category.id, list);
      }
    });
    return map;
  }, [categories]);

  const manualFormSubcategories = useMemo(() => {
    if (!manualFormState.categoryId) {
      return [];
    }
    const parsed = Number(manualFormState.categoryId);
    if (!Number.isFinite(parsed)) {
      return [];
    }
    return subcategoriesByCategory.get(parsed) || [];
  }, [manualFormState.categoryId, subcategoriesByCategory]);

  const filterSubcategories = useMemo(() => {
    if (!categoryFilter || categoryFilter === 'none') {
      return [];
    }
    const parsed = Number(categoryFilter);
    if (!Number.isFinite(parsed)) {
      return [];
    }
    return subcategoriesByCategory.get(parsed) || [];
  }, [categoryFilter, subcategoriesByCategory]);

  const editingSubcategories = useMemo(() => {
    if (!editingTransactionCategoryId) {
      return [];
    }
    const parsed = Number(editingTransactionCategoryId);
    if (!Number.isFinite(parsed)) {
      return [];
    }
    return subcategoriesByCategory.get(parsed) || [];
  }, [editingTransactionCategoryId, subcategoriesByCategory]);

  const flattenedSubcategories = useMemo(() => {
    const list = [];
    categories.forEach((category) => {
      if (category && typeof category.id === 'number') {
        const subs = Array.isArray(category.subcategories)
          ? category.subcategories
          : [];
        subs.forEach((sub) => {
          if (sub && typeof sub.id === 'number') {
            list.push({
              ...sub,
              categoryId: category.id,
              categoryName: category.name,
            });
          }
        });
      }
    });
    return list;
  }, [categories]);

  const formatChartCurrency = useCallback((rawValue) => {
    const numeric = typeof rawValue === 'number' ? rawValue : Number(rawValue || 0);
    if (!Number.isFinite(numeric)) {
      return formatCurrency(0);
    }
    return formatCurrency(numeric);
  }, []);

  const categoryMovementChart = useMemo(() => {
    if (!categoryReportData || !Array.isArray(categoryReportData.categories)) {
      return null;
    }

    const categoriesWithMovement = categoryReportData.categories
      .map((category) => ({
        ...category,
        movement:
          Number(category?.totalCredits || 0) + Number(category?.totalDebits || 0),
      }))
      .filter((entry) => entry.movement > 0);

    if (categoriesWithMovement.length === 0) {
      return null;
    }

    return {
      labels: categoriesWithMovement.map((entry) => entry.name),
      datasets: [
        {
          label: 'Movimentação total (R$)',
          data: categoriesWithMovement.map((entry) => entry.movement),
          backgroundColor: categoriesWithMovement.map((entry, index) =>
            getColorByIndex(entry.color, index),
          ),
          borderWidth: 1,
        },
      ],
    };
  }, [categoryReportData]);

  const categoryMovementChartOptions = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (context) =>
              `${context.label}: ${formatChartCurrency(context.parsed)}`,
          },
        },
      },
    }),
    [formatChartCurrency],
  );

  const categoryBalanceChart = useMemo(() => {
    if (!categoryReportData || !Array.isArray(categoryReportData.categories)) {
      return null;
    }

    const labels = categoryReportData.categories.map((category) => category.name);
    if (labels.length === 0) {
      return null;
    }

    const creditData = categoryReportData.categories.map((category) =>
      Number(category?.totalCredits || 0),
    );
    const debitData = categoryReportData.categories.map((category) =>
      Number(category?.totalDebits || 0),
    );

    const hasValues =
      creditData.some((value) => value > 0) || debitData.some((value) => value > 0);
    if (!hasValues) {
      return null;
    }

    return {
      labels,
      datasets: [
        {
          label: 'Receitas (R$)',
          data: creditData,
          backgroundColor: 'rgba(16, 185, 129, 0.6)',
          borderColor: 'rgba(5, 150, 105, 1)',
          borderWidth: 1,
        },
        {
          label: 'Despesas (R$)',
          data: debitData,
          backgroundColor: 'rgba(248, 113, 113, 0.6)',
          borderColor: 'rgba(220, 38, 38, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [categoryReportData]);

  const categoryBalanceChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value =
                typeof context.parsed === 'object'
                  ? context.parsed.y ?? 0
                  : context.parsed;
              return `${context.dataset.label}: ${formatChartCurrency(value)}`;
            },
          },
        },
      },
      scales: {
        y: {
          ticks: {
            callback: (value) => formatChartCurrency(value),
          },
        },
      },
    }),
    [formatChartCurrency],
  );

  const topSubcategoryChart = useMemo(() => {
    if (!categoryReportData || !Array.isArray(categoryReportData.categories)) {
      return null;
    }

    const entries = [];
    categoryReportData.categories.forEach((category) => {
      const subcategories = Array.isArray(category?.subcategories)
        ? category.subcategories
        : [];
      subcategories.forEach((subcategory) => {
        const totalCredits = Number(subcategory?.totalCredits || 0);
        const totalDebits = Number(subcategory?.totalDebits || 0);
        const totalMovement = totalCredits + totalDebits;
        if (totalMovement <= 0) {
          return;
        }
        entries.push({
          label: `${category.name} • ${subcategory.name}`,
          movement: totalMovement,
          color: subcategory.color ?? category.color ?? null,
        });
      });
    });

    entries.sort((a, b) => b.movement - a.movement);
    const topEntries = entries.slice(0, 10);
    if (topEntries.length === 0) {
      return null;
    }

    return {
      labels: topEntries.map((entry) => entry.label),
      datasets: [
        {
          label: 'Movimentação total (R$)',
          data: topEntries.map((entry) => entry.movement),
          backgroundColor: topEntries.map((entry, index) =>
            getColorByIndex(entry.color, index),
          ),
          borderWidth: 1,
        },
      ],
    };
  }, [categoryReportData]);

  const topSubcategoryChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom' },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value =
                typeof context.parsed === 'object'
                  ? context.parsed.y ?? 0
                  : context.parsed;
              return `${formatChartCurrency(value)}`;
            },
          },
        },
      },
      scales: {
        y: {
          ticks: {
            callback: (value) => formatChartCurrency(value),
          },
        },
      },
    }),
    [formatChartCurrency],
  );

  const categoryReportGeneratedAt = useMemo(() => {
    if (!categoryReportData?.generatedAt) {
      return '';
    }
    const parsed = new Date(categoryReportData.generatedAt);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    return parsed.toLocaleString('pt-BR');
  }, [categoryReportData]);

  const hasCategoryReportData = useMemo(
    () =>
      Array.isArray(categoryReportData?.categories) &&
      categoryReportData.categories.length > 0,
    [categoryReportData],
  );

  const categoryReportTotals = useMemo(() => {
    if (!categoryReportData) {
      return { credits: 0, debits: 0, balance: 0 };
    }
    return {
      credits: Number(categoryReportData.totalCredits || 0),
      debits: Number(categoryReportData.totalDebits || 0),
      balance: Number(categoryReportData.balance || 0),
    };
  }, [categoryReportData]);

  const showAlert = useCallback((type, message) => {
    setAlert({ type, message });
  }, []);

  const loadAccounts = useCallback(async () => {
    setLoadingAccounts(true);
    setAccountsError('');
    try {
      const { data } = await FinanceService.listAccounts();
      const list = Array.isArray(data) ? sortAccountsByName(data) : [];
      setAccounts(list);
      setSelectedAccountId((current) => {
        if (current && list.some((account) => account.id === current)) {
          return current;
        }
        return list.length > 0 ? list[0].id : null;
      });
    } catch (error) {
      setAccounts([]);
      setSelectedAccountId(null);
      const message = resolveErrorMessage(error);
      setAccountsError(message);
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    setCategoriesLoading(true);
    setCategoryError('');
    try {
      const { data } = await FinanceService.listCategories();
      const list = Array.isArray(data) ? data : [];
      setCategories(list);
    } catch (error) {
      setCategories([]);
      setCategoryError(resolveErrorMessage(error));
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  const handleCategoryFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setCategoryFormState((prev) => ({ ...prev, [name]: value }));
    setCategoryError('');
  }, []);

  const handleSubcategoryFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setSubcategoryFormState((prev) => ({ ...prev, [name]: value }));
    setSubcategoryError('');
  }, []);

  const handleCategoryFilterChange = useCallback(
    (event) => {
      const { value } = event.target;
      setCategoryFilter(value);
      if (!value || value === 'none') {
        setSubcategoryFilter('');
        return;
      }
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) {
        setSubcategoryFilter('');
        return;
      }
      const available = subcategoriesByCategory.get(parsed) || [];
      setSubcategoryFilter((previous) => {
        if (!previous || previous === 'none') {
          return previous;
        }
        const parsedPrevious = Number(previous);
        if (!Number.isFinite(parsedPrevious)) {
          return '';
        }
        return available.some((item) => item && item.id === parsedPrevious)
          ? previous
          : '';
      });
    },
    [subcategoriesByCategory],
  );

  const handleSubcategoryFilterChange = useCallback((event) => {
    setSubcategoryFilter(event.target.value);
  }, []);

  const handleCreateCategory = useCallback(
    async (event) => {
      event.preventDefault();
      if (!canManage) return;
      const name = categoryFormState.name.trim();
      if (!name) {
        setCategoryError('Informe o nome da categoria.');
        return;
      }

      setCategorySubmitting(true);
      setCategoryError('');
      try {
        await FinanceService.createCategory({
          name,
          color: categoryFormState.color?.trim() || null,
          description: categoryFormState.description?.trim() || null,
        });
        showAlert('success', 'Categoria criada com sucesso.');
        setCategoryFormState({ name: '', color: '', description: '' });
        await loadCategories();
      } catch (error) {
        setCategoryError(resolveErrorMessage(error));
      } finally {
        setCategorySubmitting(false);
      }
    },
    [canManage, categoryFormState, loadCategories, showAlert],
  );

  const handleCreateSubcategory = useCallback(
    async (event) => {
      event.preventDefault();
      if (!canManage) return;
      const categoryIdValue = Number(subcategoryFormState.categoryId);
      if (!Number.isFinite(categoryIdValue) || categoryIdValue <= 0) {
        setSubcategoryError('Selecione a categoria.');
        return;
      }
      const name = subcategoryFormState.name.trim();
      if (!name) {
        setSubcategoryError('Informe o nome da subcategoria.');
        return;
      }

      setSubcategorySubmitting(true);
      setSubcategoryError('');
      try {
        await FinanceService.createSubcategory(categoryIdValue, {
          name,
          color: subcategoryFormState.color?.trim() || null,
          description: subcategoryFormState.description?.trim() || null,
        });
        showAlert('success', 'Subcategoria criada com sucesso.');
        setSubcategoryFormState({ categoryId: '', name: '', color: '', description: '' });
        await loadCategories();
      } catch (error) {
        setSubcategoryError(resolveErrorMessage(error));
      } finally {
        setSubcategorySubmitting(false);
      }
    },
    [canManage, loadCategories, showAlert, subcategoryFormState],
  );

  const handleDeleteCategory = useCallback(
    async (id) => {
      if (!canManage) return;
      if (typeof window !== 'undefined') {
        const confirmed = window.confirm('Deseja realmente excluir esta categoria?');
        if (!confirmed) return;
      }
      setCategoryDeletingId(id);
      try {
        await FinanceService.deleteCategory(id);
        showAlert('success', 'Categoria removida com sucesso.');
        setCategoryFilter((prev) => {
          if (!prev || prev === 'none') {
            return prev;
          }
          return Number(prev) === Number(id) ? '' : prev;
        });
        setSubcategoryFilter('');
        await loadCategories();
      } catch (error) {
        showAlert('error', resolveErrorMessage(error));
      } finally {
        setCategoryDeletingId(null);
      }
    },
    [canManage, loadCategories, showAlert],
  );

  const handleDeleteSubcategory = useCallback(
    async (id) => {
      if (!canManage) return;
      if (typeof window !== 'undefined') {
        const confirmed = window.confirm('Deseja realmente excluir esta subcategoria?');
        if (!confirmed) return;
      }
      setSubcategoryDeletingId(id);
      try {
        await FinanceService.deleteSubcategory(id);
        showAlert('success', 'Subcategoria removida com sucesso.');
        setSubcategoryFilter((prev) => {
          if (!prev || prev === 'none') {
            return prev;
          }
          return Number(prev) === Number(id) ? '' : prev;
        });
        await loadCategories();
      } catch (error) {
        showAlert('error', resolveErrorMessage(error));
      } finally {
        setSubcategoryDeletingId(null);
      }
    },
    [canManage, loadCategories, showAlert],
  );

  const handleEditCategory = useCallback(
    async (category) => {
      if (!canManage || !category || typeof category.id !== 'number') return;
      if (typeof window === 'undefined') return;
      const namePrompt = window.prompt('Nome da categoria', category.name || '');
      if (namePrompt === null) return;
      const normalizedName = namePrompt.trim();
      if (!normalizedName) {
        showAlert('error', 'Informe um nome válido.');
        return;
      }
      const colorPrompt = window.prompt(
        'Cor hexadecimal (opcional, ex: #FFAA00)',
        category.color || '',
      );
      if (colorPrompt === null) return;
      const descriptionPrompt = window.prompt(
        'Descrição (opcional)',
        category.description || '',
      );
      if (descriptionPrompt === null) return;

      setCategoryUpdatingId(category.id);
      try {
        await FinanceService.updateCategory(category.id, {
          name: normalizedName,
          color: colorPrompt?.trim() || null,
          description: descriptionPrompt?.trim() || null,
        });
        showAlert('success', 'Categoria atualizada com sucesso.');
        await loadCategories();
      } catch (error) {
        showAlert('error', resolveErrorMessage(error));
      } finally {
        setCategoryUpdatingId(null);
      }
    },
    [canManage, loadCategories, showAlert],
  );

  const handleEditSubcategory = useCallback(
    async (subcategory) => {
      if (!canManage || !subcategory || typeof subcategory.id !== 'number') return;
      if (typeof window === 'undefined') return;
      const namePrompt = window.prompt('Nome da subcategoria', subcategory.name || '');
      if (namePrompt === null) return;
      const normalizedName = namePrompt.trim();
      if (!normalizedName) {
        showAlert('error', 'Informe um nome válido.');
        return;
      }
      const colorPrompt = window.prompt(
        'Cor hexadecimal (opcional, ex: #FFAA00)',
        subcategory.color || '',
      );
      if (colorPrompt === null) return;
      const descriptionPrompt = window.prompt(
        'Descrição (opcional)',
        subcategory.description || '',
      );
      if (descriptionPrompt === null) return;

      setSubcategoryUpdatingId(subcategory.id);
      try {
        await FinanceService.updateSubcategory(subcategory.id, {
          name: normalizedName,
          color: colorPrompt?.trim() || null,
          description: descriptionPrompt?.trim() || null,
          categoryId: subcategory.categoryId,
        });
        showAlert('success', 'Subcategoria atualizada com sucesso.');
        await loadCategories();
      } catch (error) {
        showAlert('error', resolveErrorMessage(error));
      } finally {
        setSubcategoryUpdatingId(null);
      }
    },
    [canManage, loadCategories, showAlert],
  );

  const handleOpenCategoryManager = useCallback(() => {
    setShowCategoryManager(true);
  }, []);

  const handleCloseCategoryManager = useCallback(() => {
    setShowCategoryManager(false);
    setCategoryError('');
    setSubcategoryError('');
  }, []);

  const handleOpenCategoryReport = useCallback(async () => {
    if (!selectedAccountId) {
      return;
    }
    setShowCategoryReport(true);
    setCategoryReportLoading(true);
    setCategoryReportError('');
    try {
      const params = {};
      const fromParam = buildDateParam(fromDate, false);
      const toParam = buildDateParam(toDate, true);
      if (fromParam) params.from = fromParam;
      if (toParam) params.to = toParam;
      if (categoryFilter) {
        params.categoryId = categoryFilter === 'none' ? 'none' : categoryFilter;
      }
      if (subcategoryFilter) {
        params.subcategoryId =
          subcategoryFilter === 'none' ? 'none' : subcategoryFilter;
      }
      const { data } = await FinanceService.getCategorySummary(selectedAccountId, params);
      setCategoryReportData(data || null);
    } catch (error) {
      setCategoryReportError(resolveErrorMessage(error));
      setCategoryReportData(null);
    } finally {
      setCategoryReportLoading(false);
    }
  }, [
    categoryFilter,
    fromDate,
    selectedAccountId,
    subcategoryFilter,
    toDate,
  ]);

  const handleCloseCategoryReport = useCallback(() => {
    setShowCategoryReport(false);
    setCategoryReportData(null);
    setCategoryReportError('');
  }, []);

  const loadTransactions = useCallback(async () => {
    if (!selectedAccountId) {
      setTransactions([]);
      setTransactionSummary(null);
      setTransactionsError('');
      setEditingTransactionId(null);
      setEditingTransactionDescription('');
      setEditingTransactionError('');
      setUpdatingTransaction(false);
      return;
    }

    setTransactionsLoading(true);
    setTransactionsError('');
    setDeletingTransactionIds([]);
    setEditingTransactionId(null);
    setEditingTransactionDescription('');
    setEditingTransactionError('');
    setUpdatingTransaction(false);
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const fromParam = buildDateParam(fromDate, false);
      const toParam = buildDateParam(toDate, true);
      if (fromParam) {
        params.from = fromParam;
      }
      if (toParam) {
        params.to = toParam;
      }
      if (categoryFilter) {
        params.categoryId = categoryFilter === 'none' ? 'none' : categoryFilter;
      }
      if (subcategoryFilter) {
        params.subcategoryId =
          subcategoryFilter === 'none' ? 'none' : subcategoryFilter;
      }
      const { data } = await FinanceService.listTransactions(selectedAccountId, params);
      const fetchedTransactions = Array.isArray(data?.transactions) ? data.transactions : [];
      setTransactions(fetchedTransactions);
      setTransactionSummary(data?.summary || null);
      setSelectedTransactionIds([]);
    } catch (error) {
      const message = resolveErrorMessage(error);
      setTransactionsError(message);
      setTransactions([]);
      setTransactionSummary(null);
    } finally {
      setTransactionsLoading(false);
    }
  }, [
    categoryFilter,
    fromDate,
    selectedAccountId,
    statusFilter,
    subcategoryFilter,
    toDate,
  ]);

  useEffect(() => {
    if (!canView) {
      setAccounts([]);
      setSelectedAccountId(null);
      return;
    }
    loadAccounts();
  }, [canView, loadAccounts]);

  useEffect(() => {
    if (!canView) {
      setCategories([]);
      return;
    }
    loadCategories();
  }, [canView, loadCategories]);

  useEffect(() => {
    if (!selectedAccountId) {
      setTransactions([]);
      setTransactionSummary(null);
      return;
    }
    loadTransactions();
  }, [loadTransactions, selectedAccountId]);

  useEffect(() => {
    setShowManualTransactionForm(false);
    setManualFormState({
      ...emptyManualTransactionForm,
      occurredAt: formatLocalDateTimeInput(new Date()),
    });
    setManualFormError('');
    setEditingTransactionId(null);
    setEditingTransactionDescription('');
    setEditingTransactionError('');
    setUpdatingTransaction(false);
    setCategoryFilter('');
    setSubcategoryFilter('');
  }, [selectedAccountId]);

  useEffect(() => {
    setManualFormState((prev) => {
      if (!prev.categoryId) {
        return prev;
      }
      const parsedCategory = Number(prev.categoryId);
      if (!Number.isFinite(parsedCategory) || !categoriesMap.has(parsedCategory)) {
        return {
          ...prev,
          categoryId: '',
          subcategoryId: '',
        };
      }
      if (!prev.subcategoryId) {
        return prev;
      }
      const parsedSubcategory = Number(prev.subcategoryId);
      const subcategories = subcategoriesByCategory.get(parsedCategory) || [];
      if (
        !Number.isFinite(parsedSubcategory) ||
        !subcategories.some((item) => item && item.id === parsedSubcategory)
      ) {
        return {
          ...prev,
          subcategoryId: '',
        };
      }
      return prev;
    });
  }, [categoriesMap, subcategoriesByCategory]);

  useEffect(() => {
    if (editingTransactionId === null) {
      return;
    }
    setEditingTransactionCategoryId((prev) => {
      if (!prev) {
        return prev;
      }
      const parsed = Number(prev);
      if (!Number.isFinite(parsed) || !categoriesMap.has(parsed)) {
        setEditingTransactionSubcategoryId('');
        return '';
      }
      return prev;
    });
    setEditingTransactionSubcategoryId((prev) => {
      if (!editingTransactionCategoryId) {
        return '';
      }
      if (!prev) {
        return '';
      }
      const parsedCategory = Number(editingTransactionCategoryId);
      const parsedSubcategory = Number(prev);
      if (!Number.isFinite(parsedCategory) || !Number.isFinite(parsedSubcategory)) {
        return '';
      }
      const subcategories = subcategoriesByCategory.get(parsedCategory) || [];
      return subcategories.some((item) => item && item.id === parsedSubcategory)
        ? prev
        : '';
    });
  }, [categoriesMap, editingTransactionCategoryId, editingTransactionId, subcategoriesByCategory]);

  useEffect(() => {
    if (!alert) return undefined;
    const timer = setTimeout(() => setAlert(null), 6000);
    return () => clearTimeout(timer);
  }, [alert]);

  const integrationLabel = useMemo(() => {
    if (!selectedAccount) return '';
    const option = INTEGRATION_TYPES.find((item) => item.value === selectedAccount.integrationType);
    return option ? option.label : 'Integração personalizada';
  }, [selectedAccount]);

  const displayedTransactions = useMemo(() => {
    if (!searchTerm) {
      return transactions;
    }
    const normalized = searchTerm.trim().toLowerCase();
    if (!normalized) {
      return transactions;
    }
    return transactions.filter((transaction) => {
      const candidates = [
        transaction.description,
        transaction.notes,
        transaction.fitId,
        transaction.externalId,
      ]
        .filter(Boolean)
        .map((value) => String(value).toLowerCase());
      return candidates.some((value) => value.includes(normalized));
    });
  }, [searchTerm, transactions]);

  const selectedIdsSet = useMemo(
    () => new Set(selectedTransactionIds.map((id) => Number(id)).filter((id) => Number.isFinite(id))),
    [selectedTransactionIds],
  );

  const deletingIdsSet = useMemo(
    () => new Set(deletingTransactionIds.map((id) => Number(id)).filter((id) => Number.isFinite(id))),
    [deletingTransactionIds],
  );

  const selectionStats = useMemo(() => {
    if (!selectedIdsSet.size) {
      return {
        count: 0,
        total: 0,
        credits: 0,
        debits: 0,
      };
    }
    let total = 0;
    let credits = 0;
    let debits = 0;
    for (const transaction of transactions) {
      if (!selectedIdsSet.has(transaction.id)) continue;
      total += Number(transaction.amount || 0);
      if (Number(transaction.amount || 0) >= 0) {
        credits += 1;
      } else {
        debits += 1;
      }
    }
    return {
      count: selectedIdsSet.size,
      total,
      credits,
      debits,
    };
  }, [selectedIdsSet, transactions]);

  const summary = useMemo(
    () => ({
      totalCredits: transactionSummary?.totalCredits || 0,
      totalDebits: transactionSummary?.totalDebits || 0,
      balance: transactionSummary?.balance || 0,
      reconciledCount: transactionSummary?.reconciledCount || 0,
      pendingCount: transactionSummary?.pendingCount || 0,
      divergentCount: transactionSummary?.divergentCount || 0,
    }),
    [transactionSummary],
  );

  const handleEditAccount = useCallback(
    (account) => {
      if (!canManage) return;
      setFormState(toFormState(account));
      setFormError('');
      setShowForm(true);
    },
    [canManage],
  );

  const handleCreateAccount = useCallback(() => {
    if (!canManage) return;
    setFormState({ ...emptyForm });
    setFormError('');
    setShowForm(true);
  }, [canManage]);

  const handleCancelForm = useCallback(() => {
    setShowForm(false);
    setFormState({ ...emptyForm });
    setFormError('');
  }, []);

  const handleFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSaveAccount = useCallback(
    async (event) => {
      event.preventDefault();
      if (!canManage) return;

      if (!formState.name.trim() || !formState.bankName.trim() || !formState.accountNumber.trim()) {
        setFormError('Informe nome, banco e número da conta.');
        return;
      }

      let integrationConfig = null;
      try {
        integrationConfig = parseIntegrationConfigText(formState.integrationConfig);
      } catch (error) {
        setFormError(error.message);
        return;
      }

      const description = formState.description.trim();

      const payload = {
        name: formState.name.trim(),
        bankName: formState.bankName.trim(),
        agencyNumber: formState.agencyNumber.trim(),
        accountNumber: formState.accountNumber.trim(),
        description: description || undefined,
        integrationType: formState.integrationType,
        integrationConfig,
      };

      setSavingAccount(true);
      setFormError('');
      try {
        if (formState.id) {
          const { data } = await FinanceService.updateAccount(formState.id, payload);
          setAccounts((prev) => sortAccountsByName(prev.map((account) => (account.id === data.id ? data : account))));
          setSelectedAccountId(data.id);
          showAlert('success', 'Conta bancária atualizada com sucesso.');
        } else {
          const { data } = await FinanceService.createAccount(payload);
          setAccounts((prev) => sortAccountsByName([...prev, data]));
          setSelectedAccountId(data.id);
          showAlert('success', 'Conta bancária criada com sucesso.');
        }
        setShowForm(false);
        setFormState({ ...emptyForm });
      } catch (error) {
        setFormError(resolveErrorMessage(error));
      } finally {
        setSavingAccount(false);
      }
    },
    [canManage, formState, showAlert],
  );

  const handleDeleteAccount = useCallback(
    async (accountId) => {
      if (!canManage) return;
      const account = accounts.find((item) => item.id === accountId);
      const confirmationMessage = account
        ? `Deseja realmente remover a conta "${account.name}"?`
        : 'Deseja realmente remover esta conta bancária?';
      if (typeof window !== 'undefined' && !window.confirm(confirmationMessage)) {
        return;
      }
      setDeletingAccountId(accountId);
      try {
        await FinanceService.deleteAccount(accountId);
        setAccounts((prev) => {
          const next = sortAccountsByName(prev.filter((item) => item.id !== accountId));
          setSelectedAccountId((current) => {
            if (!current || !next.some((item) => item.id === current) || current === accountId) {
              return next.length > 0 ? next[0].id : null;
            }
            return current;
          });
          return next;
        });
        setSelectedTransactionIds([]);
        showAlert('success', 'Conta bancária removida com sucesso.');
      } catch (error) {
        showAlert('error', resolveErrorMessage(error));
      } finally {
        setDeletingAccountId(null);
      }
    },
    [accounts, canManage, showAlert],
  );

  const handleSelectAccount = useCallback((accountId) => {
    setSelectedAccountId(accountId);
    setShowForm(false);
    setFormState({ ...emptyForm });
    setSelectedTransactionIds([]);
    setTransactionsError('');
    setAlert(null);
  }, []);

  const handleToggleTransactionSelection = useCallback((transactionId) => {
    setSelectedTransactionIds((prev) => {
      if (prev.includes(transactionId)) {
        return prev.filter((id) => id !== transactionId);
      }
      return [...prev, transactionId];
    });
  }, []);

  const handleSelectAllDisplayed = useCallback(() => {
    const displayedIds = displayedTransactions.map((transaction) => transaction.id);
    const allSelected = displayedIds.every((id) => selectedIdsSet.has(id));
    if (allSelected) {
      setSelectedTransactionIds((prev) => prev.filter((id) => !displayedIds.includes(id)));
    } else {
      const next = new Set(selectedTransactionIds);
      displayedIds.forEach((id) => next.add(id));
      setSelectedTransactionIds(Array.from(next));
    }
  }, [displayedTransactions, selectedIdsSet, selectedTransactionIds]);

  const handleClearSelection = useCallback(() => {
    setSelectedTransactionIds([]);
  }, []);

  const triggerFileDialog = useCallback(() => {
    if (!canManage) return;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }, [canManage]);

  const handleOfxFileChange = useCallback(
    async (event) => {
      if (!canManage) return;
      if (!selectedAccountId) return;
      const file = event.target.files?.[0];
      if (!file) return;
      setImportingOfx(true);
      try {
        const { data } = await FinanceService.importOfx(selectedAccountId, file);
        const imported = Number(data?.importedCount || 0);
        const skipped = Number(data?.skippedCount || 0);
        const messageParts = [];
        if (imported > 0) {
          messageParts.push(`${imported} transações importadas`);
        }
        if (skipped > 0) {
          messageParts.push(`${skipped} duplicadas ignoradas`);
        }
        showAlert('success', messageParts.length ? messageParts.join(' · ') : 'Importação concluída.');
        await loadTransactions();
      } catch (error) {
        showAlert('error', resolveErrorMessage(error));
      } finally {
        setImportingOfx(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [canManage, loadTransactions, selectedAccountId, showAlert],
  );

  const handleOpenManualForm = useCallback(() => {
    if (!canManage) return;
    setShowManualTransactionForm(true);
    setManualFormError('');
    setManualFormState((prev) => ({
      ...prev,
      occurredAt: prev.occurredAt || formatLocalDateTimeInput(new Date()),
    }));
  }, [canManage]);

  const handleCloseManualForm = useCallback(() => {
    setShowManualTransactionForm(false);
    setManualFormError('');
    setManualFormState({
      ...emptyManualTransactionForm,
      occurredAt: formatLocalDateTimeInput(new Date()),
    });
  }, []);

  const handleManualFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setManualFormState((prev) => {
      if (name === 'categoryId') {
        return {
          ...prev,
          categoryId: value,
          subcategoryId: '',
        };
      }
      if (name === 'subcategoryId') {
        return { ...prev, subcategoryId: value };
      }
      return { ...prev, [name]: value };
    });
    setManualFormError('');
  }, []);

  const handleManualFormSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (!canManage || !selectedAccountId) return;

      const type = manualFormState.type === 'CREDIT' ? 'CREDIT' : 'DEBIT';
      const amountNumber = parseAmountInput(manualFormState.amount);
      if (!Number.isFinite(amountNumber) || Math.abs(amountNumber) === 0) {
        setManualFormError('Informe um valor válido para a transação.');
        return;
      }

      if (!manualFormState.occurredAt) {
        setManualFormError('Informe a data e hora da transação.');
        return;
      }

      const occurredAtDate = new Date(manualFormState.occurredAt);
      if (Number.isNaN(occurredAtDate.getTime())) {
        setManualFormError('Informe uma data válida para a transação.');
        return;
      }

      const description = manualFormState.description
        ? manualFormState.description.trim()
        : '';
      const notes = manualFormState.notes ? manualFormState.notes.trim() : '';

      let categoryIdValue = null;
      if (manualFormState.categoryId) {
        const parsed = Number(manualFormState.categoryId);
        if (!Number.isFinite(parsed) || parsed <= 0) {
          setManualFormError('Selecione uma categoria válida.');
          return;
        }
        categoryIdValue = parsed;
      }

      let subcategoryIdValue = null;
      if (manualFormState.subcategoryId) {
        const parsed = Number(manualFormState.subcategoryId);
        if (!Number.isFinite(parsed) || parsed <= 0) {
          setManualFormError('Selecione uma subcategoria válida.');
          return;
        }
        subcategoryIdValue = parsed;
      }

      setCreatingManualTransaction(true);
      setManualFormError('');
      try {
        const payload = {
          type,
          amount: amountNumber,
          occurredAt: occurredAtDate.toISOString(),
          description: description || undefined,
          notes: notes || undefined,
          categoryId: categoryIdValue,
          subcategoryId: subcategoryIdValue,
        };
        const { data } = await FinanceService.createManualTransaction(
          selectedAccountId,
          payload,
        );
        if (data?.summary) {
          setTransactionSummary(data.summary);
        }
        await loadTransactions();
        showAlert('success', 'Transação manual adicionada com sucesso.');
        setShowManualTransactionForm(false);
        setManualFormState({
          ...emptyManualTransactionForm,
          occurredAt: formatLocalDateTimeInput(new Date()),
        });
      } catch (error) {
        setManualFormError(resolveErrorMessage(error));
      } finally {
        setCreatingManualTransaction(false);
      }
    },
    [
      canManage,
      loadTransactions,
      manualFormState,
      selectedAccountId,
      showAlert,
    ],
  );

  const handleDeleteTransaction = useCallback(
    async (transactionId) => {
      if (!canManage || !selectedAccountId) return;

      const transaction = transactions.find((item) => item.id === transactionId);
      if (!transaction || transaction.source !== 'MANUAL') {
        showAlert('error', 'Apenas transações manuais podem ser excluídas.');
        return;
      }
      const confirmationMessage = transaction?.description
        ? `Deseja realmente excluir a transação "${transaction.description}"?`
        : 'Deseja realmente excluir esta transação manual?';
      if (typeof window !== 'undefined' && !window.confirm(confirmationMessage)) {
        return;
      }

      setDeletingTransactionIds((prev) => [...prev, transactionId]);
      try {
        const { data } = await FinanceService.deleteTransaction(
          selectedAccountId,
          transactionId,
        );
        setTransactions((prev) => prev.filter((item) => item.id !== transactionId));
        setSelectedTransactionIds((prev) => prev.filter((id) => id !== transactionId));
        if (data?.summary) {
          setTransactionSummary(data.summary);
        }
        showAlert('success', 'Transação manual excluída com sucesso.');
      } catch (error) {
        showAlert('error', resolveErrorMessage(error));
      } finally {
        setDeletingTransactionIds((prev) => prev.filter((id) => id !== transactionId));
      }
    },
    [canManage, selectedAccountId, showAlert, transactions],
  );

  const handleCancelEditingTransaction = useCallback(() => {
    setEditingTransactionId(null);
    setEditingTransactionDescription('');
    setEditingTransactionError('');
    setUpdatingTransaction(false);
    setEditingTransactionCategoryId('');
    setEditingTransactionSubcategoryId('');
  }, []);

  const handleStartEditingTransaction = useCallback(
    (transaction) => {
      if (!canManage || updatingTransaction) {
        return;
      }
      setEditingTransactionId(transaction.id);
      setEditingTransactionDescription(transaction.description || '');
      setEditingTransactionError('');
      setEditingTransactionCategoryId(
        transaction.category && typeof transaction.category.id === 'number'
          ? String(transaction.category.id)
          : '',
      );
      setEditingTransactionSubcategoryId(
        transaction.subcategory && typeof transaction.subcategory.id === 'number'
          ? String(transaction.subcategory.id)
          : '',
      );
    },
    [canManage, updatingTransaction],
  );

  const handleEditingTransactionCategoryChange = useCallback((event) => {
    const { value } = event.target;
    setEditingTransactionCategoryId(value);
    setEditingTransactionSubcategoryId('');
  }, []);

  const handleEditingTransactionSubcategoryChange = useCallback((event) => {
    setEditingTransactionSubcategoryId(event.target.value);
  }, []);

  const handleUpdateTransaction = useCallback(async () => {
    if (!canManage || !selectedAccountId || editingTransactionId === null) {
      return;
    }
    setUpdatingTransaction(true);
    setEditingTransactionError('');
    try {
      const { data } = await FinanceService.updateTransaction(
        selectedAccountId,
        editingTransactionId,
        {
          description: editingTransactionDescription,
          categoryId: editingTransactionCategoryId
            ? Number(editingTransactionCategoryId)
            : null,
          subcategoryId: editingTransactionSubcategoryId
            ? Number(editingTransactionSubcategoryId)
            : null,
        },
      );
      const updatedTransaction = data?.transaction || null;
      setTransactions((prev) =>
        prev.map((item) => {
          if (item.id !== editingTransactionId) {
            return item;
          }
          if (updatedTransaction) {
            return {
              ...item,
              description: updatedTransaction.description ?? null,
              category: updatedTransaction.category ?? null,
              subcategory: updatedTransaction.subcategory ?? null,
            };
          }
          const trimmed = editingTransactionDescription.trim();
          return {
            ...item,
            description: trimmed ? trimmed : null,
            category: editingTransactionCategoryId
              ? categoriesMap.get(Number(editingTransactionCategoryId)) || null
              : null,
            subcategory: editingTransactionSubcategoryId
              ? (editingSubcategories.find(
                  (sub) => sub && Number(sub.id) === Number(editingTransactionSubcategoryId),
                ) || null)
              : null,
          };
        }),
      );
      showAlert('success', 'Transação atualizada com sucesso.');
      handleCancelEditingTransaction();
    } catch (error) {
      setEditingTransactionError(resolveErrorMessage(error));
    } finally {
      setUpdatingTransaction(false);
    }
  }, [
    canManage,
    categoriesMap,
    editingTransactionDescription,
    editingTransactionId,
    editingTransactionCategoryId,
    editingTransactionSubcategoryId,
    editingSubcategories,
    handleCancelEditingTransaction,
    selectedAccountId,
    showAlert,
  ]);

  const handleSyncAccount = useCallback(
    async () => {
      if (!canManage || !selectedAccountId) return;
      setSyncingAccount(true);
      try {
        await FinanceService.syncAccount(selectedAccountId);
        showAlert('success', 'Sincronização solicitada com sucesso.');
        await loadTransactions();
      } catch (error) {
        showAlert('error', resolveErrorMessage(error));
      } finally {
        setSyncingAccount(false);
      }
    },
    [canManage, loadTransactions, selectedAccountId, showAlert],
  );

  const handleReconcile = useCallback(
    async () => {
      if (!canManage || !selectedAccountId || selectedTransactionIds.length === 0) {
        return;
      }
      setReconciling(true);
      try {
        const payload = {
          matchedTransactionIds: selectedTransactionIds,
          markOthersAsDivergent,
          notes: reconciliationNotes?.trim() ? reconciliationNotes.trim() : null,
        };
        const { data } = await FinanceService.reconcile(selectedAccountId, payload);
        const matched = Number(data?.matchedCount || selectedTransactionIds.length);
        const divergent = Number(data?.divergentCount || 0);
        const parts = [`${matched} transações conciliadas`];
        if (markOthersAsDivergent && divergent > 0) {
          parts.push(`${divergent} marcadas como divergentes`);
        }
        showAlert('success', parts.join(' · '));
        setReconciliationNotes('');
        setMarkOthersAsDivergent(false);
        setSelectedTransactionIds([]);
        await loadTransactions();
      } catch (error) {
        showAlert('error', resolveErrorMessage(error));
      } finally {
        setReconciling(false);
      }
    },
    [canManage, loadTransactions, markOthersAsDivergent, reconciliationNotes, selectedAccountId, selectedTransactionIds, showAlert],
  );

  const handleDownloadReport = useCallback(
    async () => {
      if (!selectedAccountId) return;
      setReportLoading(true);
      try {
        const params = {};
        const fromParam = buildDateParam(fromDate, false);
        const toParam = buildDateParam(toDate, true);
        if (fromParam) params.from = fromParam;
        if (toParam) params.to = toParam;
        const response = await FinanceService.downloadReport(selectedAccountId, params);
        if (typeof window === 'undefined') {
          showAlert('success', 'Relatório gerado.');
          return;
        }
        const blobUrl = window.URL.createObjectURL(response.data);
        const disposition = response.headers?.['content-disposition'] || '';
        const match = /filename="?([^";]+)"?/i.exec(disposition);
        const filename = match ? match[1] : `relatorio-financeiro-${selectedAccountId}.html`;
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(blobUrl);
        showAlert('success', 'Relatório gerado com sucesso.');
      } catch (error) {
        showAlert('error', resolveErrorMessage(error));
      } finally {
        setReportLoading(false);
      }
    },
    [fromDate, selectedAccountId, showAlert, toDate],
  );

  if (!canView) {
    return (
      <div className="p-6">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-rose-700">
          <FaExclamationTriangle className="mr-2 inline h-5 w-5 align-text-bottom" />
          Você não possui permissão para acessar o módulo financeiro.
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 p-6 ${isDarkTheme ? 'text-slate-100' : 'text-slate-900'}`}>
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1
            className={`flex items-center gap-2 text-2xl font-semibold ${
              isDarkTheme ? 'text-slate-100' : 'text-slate-900'
            }`}
          >
            <FaMoneyCheckAlt className="h-7 w-7 text-emerald-600" />
            Financeiro
          </h1>
          <p className={`text-sm ${isDarkTheme ? 'text-slate-300' : 'text-slate-600'}`}>
            Importe extratos bancários, realize conciliações e gere relatórios com o padrão Nash.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
          <button
            type="button"
            onClick={handleOpenCategoryManager}
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-300 ${
              isDarkTheme
                ? 'border-emerald-700 bg-slate-900 text-emerald-300 hover:bg-slate-800'
                : 'border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50'
            }`}
          >
            <FaSitemap className="h-4 w-4" />
            Abrir gerenciador de categorias
          </button>
          {canManage && (
            <button
              type="button"
              onClick={handleCreateAccount}
              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <FaPlus className="h-4 w-4" />
              Nova conta bancária
            </button>
          )}
        </div>
      </div>

      {alert && (
        <div
          className={`flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm ${
            alert.type === 'error'
              ? 'border-rose-200 bg-rose-50 text-rose-700'
              : 'border-emerald-200 bg-emerald-50 text-emerald-700'
          }`}
        >
          {alert.type === 'error' ? (
            <FaExclamationTriangle className="mt-0.5 h-4 w-4" />
          ) : (
            <FaCheckCircle className="mt-0.5 h-4 w-4" />
          )}
          <span>{alert.message}</span>
        </div>
      )}

      <div className="space-y-6">
        <div
          className={`rounded-2xl border shadow-sm ${
            isDarkTheme ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-white'
          }`}
        >
          <div
            className={`flex items-center justify-between border-b px-4 py-3 ${
              isDarkTheme ? 'border-slate-700' : 'border-slate-100'
            }`}
          >
            <h2 className={`text-sm font-semibold uppercase tracking-wide ${isDarkTheme ? 'text-slate-300' : 'text-slate-600'}`}>Contas bancárias</h2>
            {loadingAccounts && <span className={`text-xs ${isDarkTheme ? 'text-slate-500' : 'text-slate-400'}`}>Carregando…</span>}
          </div>
          <div className="max-h-[480px] space-y-1 overflow-y-auto p-2">
            {accounts.map((account) => (
              <button
                key={account.id}
                type="button"
                onClick={() => handleSelectAccount(account.id)}
                className={`w-full rounded-xl px-4 py-3 text-left transition focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                  selectedAccountId === account.id
                    ? isDarkTheme
                      ? 'bg-emerald-900/30 text-emerald-200 shadow'
                      : 'bg-emerald-50 text-emerald-800 shadow'
                    : isDarkTheme
                      ? 'bg-slate-800 text-slate-200 hover:bg-slate-700'
                      : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FaUniversity className="h-4 w-4" />
                    <div>
                      <p className="text-sm font-semibold">{account.name}</p>
                      <p className={`text-xs ${isDarkTheme ? 'text-slate-300' : 'text-slate-500'}`}>{account.bankName}</p>
                    </div>
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleEditAccount(account);
                        }}
                        className={`rounded-full p-2 text-xs shadow focus:outline-none focus:ring-2 focus:ring-emerald-500 ${
                          isDarkTheme
                            ? 'bg-slate-900/80 text-slate-300 hover:text-emerald-300'
                            : 'bg-white/80 text-slate-500 hover:text-emerald-600'
                        }`}
                      >
                        <FaSave className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        disabled={deletingAccountId === account.id}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDeleteAccount(account.id);
                        }}
                        className={`rounded-full p-2 text-xs shadow focus:outline-none focus:ring-2 focus:ring-rose-500 disabled:opacity-50 ${
                          isDarkTheme
                            ? 'bg-slate-900/80 text-rose-300 hover:bg-rose-900/30'
                            : 'bg-white/80 text-rose-500 hover:bg-rose-50'
                        }`}
                      >
                        <FaTrash className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
                <p className={`mt-2 text-xs ${isDarkTheme ? 'text-slate-300' : 'text-slate-500'}`}>
                  Agência {account.agencyNumber || '-'} • Conta {account.accountNumber}
                </p>
              </button>
            ))}
            {!loadingAccounts && accounts.length === 0 && (
              <div
                className={`rounded-xl border border-dashed p-4 text-center text-sm ${
                  isDarkTheme ? 'border-slate-600 text-slate-300' : 'border-slate-200 text-slate-500'
                }`}
              >
                Nenhuma conta cadastrada.
                {canManage && ' Clique em "Nova conta bancária" para adicionar a primeira.'}
              </div>
            )}
            {accountsError && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
                {accountsError}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          {selectedAccount ? (
            <>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">{selectedAccount.name}</h2>
                    <p className="text-sm text-slate-500">
                      {selectedAccount.bankName} • Agência {selectedAccount.agencyNumber || '-'} • Conta {selectedAccount.accountNumber}
                    </p>
                    <p className="mt-1 text-xs text-slate-400">{integrationLabel}</p>
                    {selectedAccount.description && (
                      <p className="mt-3 max-w-2xl text-sm text-slate-600 whitespace-pre-line">
                        {selectedAccount.description}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {canManage && (
                      <button
                        type="button"
                        onClick={handleOpenManualForm}
                        disabled={creatingManualTransaction}
                        className="inline-flex items-center gap-2 rounded-full border border-emerald-200 px-4 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-300 disabled:opacity-50"
                      >
                        <FaKeyboard className="h-3.5 w-3.5" />
                        Lançar transação manual
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleDownloadReport}
                      disabled={reportLoading}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:opacity-50"
                    >
                      <FaFileDownload className="h-3.5 w-3.5" />
                      {reportLoading ? 'Gerando…' : 'Exportar relatório'}
                    </button>
                    <button
                      type="button"
                      onClick={handleOpenCategoryReport}
                      disabled={!selectedAccountId || categoryReportLoading}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:opacity-50"
                    >
                      <FaChartPie className="h-3.5 w-3.5" />
                      {categoryReportLoading ? 'Carregando…' : 'Resumo por categoria'}
                    </button>
                    {canManage && (
                      <>
                        <button
                          type="button"
                          onClick={triggerFileDialog}
                          disabled={importingOfx}
                          className="inline-flex items-center gap-2 rounded-full border border-emerald-200 px-4 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-300 disabled:opacity-50"
                        >
                          <FaFileImport className="h-3.5 w-3.5" />
                          {importingOfx ? 'Importando…' : 'Importar OFX'}
                        </button>
                        <button
                          type="button"
                          onClick={handleSyncAccount}
                          disabled={syncingAccount || selectedAccount.integrationType !== 'API'}
                          className="inline-flex items-center gap-2 rounded-full border border-emerald-200 px-4 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-300 disabled:opacity-50"
                        >
                          <FaSync className="h-3.5 w-3.5" />
                          {syncingAccount ? 'Sincronizando…' : 'Sincronizar conta'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".ofx,application/x-ofx"
                  className="hidden"
                  onChange={handleOfxFileChange}
                />

                <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total créditos</p>
                    <p className="mt-2 text-lg font-semibold text-emerald-700">{formatCurrency(summary.totalCredits)}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total débitos</p>
                    <p className="mt-2 text-lg font-semibold text-rose-600">{formatCurrency(summary.totalDebits)}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Saldo</p>
                    <p className={`mt-2 text-lg font-semibold ${summary.balance >= 0 ? 'text-emerald-700' : 'text-rose-600'}`}>
                      {formatCurrency(summary.balance)}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Conciliadas</p>
                    <p className="mt-2 text-lg font-semibold text-emerald-700">{summary.reconciledCount}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pendentes</p>
                    <p className="mt-2 text-lg font-semibold text-amber-600">{summary.pendingCount}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Divergentes</p>
                    <p className="mt-2 text-lg font-semibold text-rose-600">{summary.divergentCount}</p>
                  </div>
                </div>
              </div>

              {canManage && showManualTransactionForm && (
                <div className="rounded-2xl border border-emerald-200 bg-white p-5 shadow-sm">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <h3 className="text-lg font-semibold text-slate-900">Nova transação manual</h3>
                    <button
                      type="button"
                      onClick={handleCloseManualForm}
                      className="text-xs font-medium text-slate-500 underline-offset-2 hover:underline"
                    >
                      Cancelar lançamento
                    </button>
                  </div>
                  <form className="mt-4 space-y-4" onSubmit={handleManualFormSubmit}>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-5">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-600" htmlFor="manual-transaction-type">
                          Tipo
                        </label>
                        <select
                          id="manual-transaction-type"
                          name="type"
                          value={manualFormState.type}
                          onChange={handleManualFormChange}
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                          required
                        >
                          {MANUAL_TRANSACTION_TYPES.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-600" htmlFor="manual-transaction-amount">
                          Valor (BRL)
                        </label>
                        <input
                          id="manual-transaction-amount"
                          name="amount"
                          value={manualFormState.amount}
                          onChange={handleManualFormChange}
                          type="text"
                          inputMode="decimal"
                          placeholder="0,00"
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-600" htmlFor="manual-transaction-occurred-at">
                          Data e hora
                        </label>
                        <input
                          id="manual-transaction-occurred-at"
                          name="occurredAt"
                          value={manualFormState.occurredAt}
                          onChange={handleManualFormChange}
                          type="datetime-local"
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                          required
                        />
                      </div>
                      <div className="space-y-2 md:col-span-3 lg:col-span-1">
                        <label className="text-sm font-medium text-slate-600" htmlFor="manual-transaction-category">
                          Categoria
                        </label>
                        <select
                          id="manual-transaction-category"
                          name="categoryId"
                          value={manualFormState.categoryId}
                          onChange={handleManualFormChange}
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                        >
                          <option value="">Sem categoria</option>
                          {categories.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2 md:col-span-3 lg:col-span-1">
                        <label className="text-sm font-medium text-slate-600" htmlFor="manual-transaction-subcategory">
                          Subcategoria
                        </label>
                        <select
                          id="manual-transaction-subcategory"
                          name="subcategoryId"
                          value={manualFormState.subcategoryId}
                          onChange={handleManualFormChange}
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                          disabled={
                            !manualFormState.categoryId || manualFormSubcategories.length === 0
                          }
                        >
                          <option value="">Sem subcategoria</option>
                          {manualFormSubcategories.map((subcategory) => (
                            <option key={subcategory.id} value={subcategory.id}>
                              {subcategory.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-600" htmlFor="manual-transaction-description">
                        Descrição
                      </label>
                      <input
                        id="manual-transaction-description"
                        name="description"
                        value={manualFormState.description}
                        onChange={handleManualFormChange}
                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                        placeholder="Descrição da movimentação"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-600" htmlFor="manual-transaction-notes">
                        Observações
                      </label>
                      <textarea
                        id="manual-transaction-notes"
                        name="notes"
                        value={manualFormState.notes}
                        onChange={handleManualFormChange}
                        rows={3}
                        className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                        placeholder="Informações adicionais sobre o lançamento"
                      />
                    </div>
                    {manualFormError && (
                      <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
                        {manualFormError}
                      </div>
                    )}
                    <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                      <button
                        type="button"
                        onClick={handleCloseManualForm}
                        className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        disabled={creatingManualTransaction}
                        className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                      >
                        <FaSave className="h-4 w-4" />
                        {creatingManualTransaction ? 'Salvando…' : 'Salvar transação'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                  <div className="flex flex-wrap gap-3">
                    <div className="space-y-1">
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-status-filter">
                        Status
                      </label>
                      <select
                        id="finance-status-filter"
                        value={statusFilter}
                        onChange={(event) => setStatusFilter(event.target.value)}
                        className="rounded-full border border-slate-200 px-4 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                      >
                        {STATUS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-from-date">
                        De
                      </label>
                      <input
                        id="finance-from-date"
                        type="date"
                        value={fromDate}
                        onChange={(event) => setFromDate(event.target.value)}
                        className="rounded-full border border-slate-200 px-4 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-to-date">
                        Até
                      </label>
                      <input
                        id="finance-to-date"
                        type="date"
                        value={toDate}
                        onChange={(event) => setToDate(event.target.value)}
                        className="rounded-full border border-slate-200 px-4 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-category-filter">
                        Categoria
                      </label>
                      <select
                        id="finance-category-filter"
                        value={categoryFilter}
                        onChange={handleCategoryFilterChange}
                        className="rounded-full border border-slate-200 px-4 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                      >
                        <option value="">Todas</option>
                        <option value="none">Sem categoria</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-subcategory-filter">
                        Subcategoria
                      </label>
                      <select
                        id="finance-subcategory-filter"
                        value={subcategoryFilter}
                        onChange={handleSubcategoryFilterChange}
                        className="rounded-full border border-slate-200 px-4 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                        disabled={!categoryFilter}
                      >
                        <option value="">Todas</option>
                        <option value="none">Sem subcategoria</option>
                        {filterSubcategories.map((subcategory) => (
                          <option key={subcategory.id} value={subcategory.id}>
                            {subcategory.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex flex-1 items-end gap-3">
                    <div className="w-full flex-1">
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-search">
                        Buscar
                      </label>
                      <input
                        id="finance-search"
                        type="search"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        placeholder="Descrição, identificador ou nota"
                        className="mt-1 w-full rounded-full border border-slate-200 px-4 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setStatusFilter('all');
                        setFromDate('');
                        setToDate('');
                        setSearchTerm('');
                        setCategoryFilter('');
                        setSubcategoryFilter('');
                      }}
                      className="h-10 rounded-full border border-slate-200 px-4 text-xs font-medium text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300"
                    >
                      Limpar filtros
                    </button>
                  </div>
                </div>
                {transactionsError && (
                  <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
                    {transactionsError}
                  </div>
                )}

                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-100 text-left text-sm">
                    <thead>
                      <tr className="text-xs uppercase tracking-wide text-slate-500">
                        {canManage && (
                          <th className="px-3 py-2">
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                              checked={
                                displayedTransactions.length > 0 &&
                                displayedTransactions.every((transaction) => selectedIdsSet.has(transaction.id))
                              }
                              onChange={handleSelectAllDisplayed}
                            />
                          </th>
                        )}
                        <th className="px-3 py-2">Data</th>
                        <th className="px-3 py-2">Descrição</th>
                        <th className="px-3 py-2 text-right">Valor</th>
                        <th className="px-3 py-2">Tipo</th>
                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2">Origem</th>
                        {canManage && <th className="px-3 py-2 text-right">Ações</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {transactionsLoading ? (
                        <tr>
                          <td colSpan={canManage ? 8 : 6} className="px-3 py-6 text-center text-sm text-slate-500">
                            Carregando transações…
                          </td>
                        </tr>
                      ) : displayedTransactions.length === 0 ? (
                        <tr>
                          <td colSpan={canManage ? 8 : 6} className="px-3 py-6 text-center text-sm text-slate-500">
                            Nenhuma transação encontrada para os filtros selecionados.
                          </td>
                        </tr>
                      ) : (
                        displayedTransactions.map((transaction) => {
                          const isEditing = editingTransactionId === transaction.id;
                          const isDeleting = deletingIdsSet.has(transaction.id);
                          const editDisabled =
                            updatingTransaction ||
                            (editingTransactionId !== null && !isEditing);

                          return (
                            <tr key={transaction.id} className="text-sm text-slate-600">
                              {canManage && (
                                <td className="px-3 py-3 align-middle">
                                  <input
                                    type="checkbox"
                                    checked={selectedIdsSet.has(transaction.id)}
                                  onChange={() => handleToggleTransactionSelection(transaction.id)}
                                  className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                                />
                              </td>
                            )}
                              <td className="px-3 py-3 align-middle text-slate-500">
                                {formatDateTime(transaction.occurredAt)}
                              </td>
                              <td className="px-3 py-3 align-middle">
                                {isEditing ? (
                                  <div className="flex flex-col gap-3">
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                      <div className="space-y-1">
                                        <label
                                          className="text-xs font-semibold uppercase tracking-wide text-emerald-700"
                                          htmlFor={`finance-transaction-category-${transaction.id}`}
                                        >
                                          Categoria
                                        </label>
                                        <select
                                          id={`finance-transaction-category-${transaction.id}`}
                                          className="w-full rounded-lg border border-emerald-200 px-3 py-2 text-sm text-slate-700 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                          value={editingTransactionCategoryId}
                                          onChange={handleEditingTransactionCategoryChange}
                                          disabled={updatingTransaction}
                                        >
                                          <option value="">Sem categoria</option>
                                          {categories.map((category) => (
                                            <option key={category.id} value={category.id}>
                                              {category.name}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                      <div className="space-y-1">
                                        <label
                                          className="text-xs font-semibold uppercase tracking-wide text-emerald-700"
                                          htmlFor={`finance-transaction-subcategory-${transaction.id}`}
                                        >
                                          Subcategoria
                                        </label>
                                        <select
                                          id={`finance-transaction-subcategory-${transaction.id}`}
                                          className="w-full rounded-lg border border-emerald-200 px-3 py-2 text-sm text-slate-700 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                          value={editingTransactionSubcategoryId}
                                          onChange={handleEditingTransactionSubcategoryChange}
                                          disabled={
                                            updatingTransaction ||
                                            !editingTransactionCategoryId ||
                                            editingSubcategories.length === 0
                                          }
                                        >
                                          <option value="">Sem subcategoria</option>
                                          {editingSubcategories.map((subcategory) => (
                                            <option key={subcategory.id} value={subcategory.id}>
                                              {subcategory.name}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    </div>
                                    <textarea
                                      value={editingTransactionDescription}
                                      onChange={(event) =>
                                        setEditingTransactionDescription(event.target.value)
                                      }
                                      rows={2}
                                      className="w-full rounded-lg border border-emerald-200 px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                                      disabled={updatingTransaction}
                                      autoFocus
                                      onKeyDown={(event) => {
                                        if (event.key === 'Escape') {
                                          event.preventDefault();
                                          handleCancelEditingTransaction();
                                        }
                                      }}
                                    />
                                    <div className="text-xs text-slate-400">
                                      {transaction.fitId && (
                                        <span className="mr-2">FIT: {transaction.fitId}</span>
                                      )}
                                      {transaction.externalId && <span>Ext: {transaction.externalId}</span>}
                                    </div>
                                    {editingTransactionError && (
                                      <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-600">
                                        {editingTransactionError}
                                      </div>
                                    )}
                                    <div className="flex flex-wrap gap-2">
                                      <button
                                        type="button"
                                        onClick={handleUpdateTransaction}
                                        disabled={updatingTransaction}
                                        className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-xs font-medium text-white shadow hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                                      >
                                        {updatingTransaction ? 'Salvando…' : 'Salvar alterações'}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={handleCancelEditingTransaction}
                                        disabled={updatingTransaction}
                                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300 disabled:opacity-50"
                                      >
                                        Cancelar
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <div className="font-medium text-slate-700">
                                      {transaction.description || 'Sem descrição'}
                                    </div>
                                    <div className="text-xs text-slate-400">
                                      {transaction.fitId && (
                                        <span className="mr-2">FIT: {transaction.fitId}</span>
                                      )}
                                      {transaction.externalId && <span>Ext: {transaction.externalId}</span>}
                                    </div>
                                  </>
                                )}
                              </td>
                              <td className="px-3 py-3 align-middle text-right font-semibold">
                                <span className={transaction.amount >= 0 ? 'text-emerald-700' : 'text-rose-600'}>
                                  {formatCurrency(transaction.amount)}
                                </span>
                              </td>
                              <td className="px-3 py-3 align-middle text-slate-500">{transaction.type || '-'}</td>
                              <td className="px-3 py-3 align-middle">
                                <span
                                  className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                                    STATUS_STYLES[transaction.status] || 'bg-slate-100 text-slate-600'
                                  }`}
                                >
                                  {transaction.status || '-'}
                                </span>
                              </td>
                              <td className="px-3 py-3 align-middle text-slate-500">
                                {SOURCE_LABELS[transaction.source] || transaction.source || '-'}
                              </td>
                              {canManage && (
                                <td className="px-3 py-3 align-middle text-right">
                                  <div className="flex flex-wrap justify-end gap-2">
                                    {isEditing ? (
                                      <span className="text-xs font-semibold text-emerald-600">Editando…</span>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => handleStartEditingTransaction(transaction)}
                                        disabled={editDisabled}
                                        className="inline-flex items-center gap-1 rounded-full border border-emerald-200 px-3 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:opacity-50"
                                      >
                                        <FaEdit className="h-3 w-3" />
                                        Editar
                                      </button>
                                    )}
                                    {transaction.source === 'MANUAL' && (
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteTransaction(transaction.id)}
                                        disabled={
                                          isDeleting ||
                                          transaction.status === 'RECONCILED' ||
                                          updatingTransaction
                                        }
                                        className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-3 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-200 disabled:opacity-50"
                                      >
                                        <FaTrash className="h-3 w-3" />
                                        {isDeleting ? 'Removendo…' : 'Excluir'}
                                      </button>
                                    )}
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {canManage && (
                  <div className="mt-6 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-slate-700">
                          Selecionadas {selectionStats.count} transações
                        </p>
                        <p className="text-xs text-slate-500">
                          Total selecionado: {formatCurrency(selectionStats.total)} • Créditos: {selectionStats.credits} • Débitos: {selectionStats.debits}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={handleSelectAllDisplayed}
                          className="rounded-full border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300"
                        >
                          Selecionar exibidas
                        </button>
                        <button
                          type="button"
                          onClick={handleClearSelection}
                          className="rounded-full border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300"
                        >
                          Limpar seleção
                        </button>
                      </div>
                    </div>
                    <div className="mt-4 space-y-3">
                      <label className="flex items-center gap-2 text-sm text-slate-600">
                        <input
                          type="checkbox"
                          checked={markOthersAsDivergent}
                          onChange={(event) => setMarkOthersAsDivergent(event.target.checked)}
                          className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        Marcar não selecionadas como divergentes
                      </label>
                      <div className="space-y-1">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-reconciliation-notes">
                          Observações da conciliação
                        </label>
                        <textarea
                          id="finance-reconciliation-notes"
                          value={reconciliationNotes}
                          onChange={(event) => setReconciliationNotes(event.target.value)}
                          rows={3}
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                          placeholder="Anote divergências ou observações relevantes"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleReconcile}
                        disabled={reconciling || selectionStats.count === 0}
                        className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                      >
                        <FaCheckCircle className="h-4 w-4" />
                        {reconciling ? 'Conciliando…' : 'Concluir conciliação'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center">
              <p className="text-sm text-slate-500">
                Selecione uma conta bancária para visualizar as transações e iniciar a conciliação.
              </p>
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
          <div className="relative w-full max-w-2xl rounded-3xl bg-white p-6 shadow-2xl">
            <button
              type="button"
              onClick={handleCancelForm}
              className="absolute right-4 top-4 inline-flex items-center justify-center rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-200"
            >
              <FaTimes className="h-4 w-4" />
              <span className="sr-only">Fechar</span>
            </button>
            <h3 className="text-xl font-semibold text-slate-900">
              {formState.id ? 'Editar conta bancária' : 'Nova conta bancária'}
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Informe os dados da conta para habilitar lançamentos e conciliações.
            </p>
            <form className="mt-4 space-y-4" onSubmit={handleSaveAccount}>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600" htmlFor="finance-account-name">
                  Nome interno
                </label>
                <input
                  id="finance-account-name"
                  name="name"
                  value={formState.name}
                  onChange={handleFormChange}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  placeholder="Conta principal"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600" htmlFor="finance-account-bank">
                  Banco
                </label>
                <input
                  id="finance-account-bank"
                  name="bankName"
                  value={formState.bankName}
                  onChange={handleFormChange}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  placeholder="Banco XYZ"
                  required
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600" htmlFor="finance-account-agency">
                    Agência
                  </label>
                  <input
                    id="finance-account-agency"
                    name="agencyNumber"
                    value={formState.agencyNumber}
                    onChange={handleFormChange}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                    placeholder="0001"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-600" htmlFor="finance-account-number">
                    Conta
                  </label>
                  <input
                    id="finance-account-number"
                    name="accountNumber"
                    value={formState.accountNumber}
                    onChange={handleFormChange}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                    placeholder="12345-6"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600" htmlFor="finance-account-description">
                  Descrição interna
                </label>
                <textarea
                  id="finance-account-description"
                  name="description"
                  value={formState.description}
                  onChange={handleFormChange}
                  rows={3}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  placeholder="Contexto sobre o uso desta conta bancária"
                />
                <p className="text-xs text-slate-400">
                  Utilize este campo para documentar o objetivo ou detalhes de controle financeiro desta conta.
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600" htmlFor="finance-account-integration">
                  Integração bancária
                </label>
                <select
                  id="finance-account-integration"
                  name="integrationType"
                  value={formState.integrationType}
                  onChange={handleFormChange}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                >
                  {INTEGRATION_TYPES.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600" htmlFor="finance-account-config">
                  Configuração da integração (JSON)
                </label>
                <textarea
                  id="finance-account-config"
                  name="integrationConfig"
                  value={formState.integrationConfig}
                  onChange={handleFormChange}
                  rows={4}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-mono focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  placeholder='{"clientId":"..."}'
                />
                <p className="text-xs text-slate-400">
                  Utilize este campo para armazenar credenciais de integração ou configurações da API do banco.
                </p>
              </div>
              {formError && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
                  {formError}
                </div>
              )}
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={handleCancelForm}
                  className="rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingAccount}
                  className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                >
                  <FaSave className="h-4 w-4" />
                  {savingAccount ? 'Salvando…' : 'Salvar conta'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCategoryManager && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
          <div className="relative w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
                  <FaTags className="h-5 w-5 text-emerald-600" />
                  Gerenciar categorias financeiras
                </h3>
                <p className="text-sm text-slate-500">
                  Cadastre categorias e subcategorias para organizar seus lançamentos e relatórios.
                </p>
              </div>
              <div className="flex items-center gap-2">
                {categoriesLoading && (
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-500">Carregando…</span>
                )}
                <button
                  type="button"
                  onClick={handleCloseCategoryManager}
                  className="inline-flex items-center justify-center rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                >
                  <FaTimes className="h-4 w-4" />
                  <span className="sr-only">Fechar</span>
                </button>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="space-y-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <FaTags className="h-4 w-4 text-emerald-600" />
                    Nova categoria
                  </h4>
                  <form className="mt-3 space-y-3" onSubmit={handleCreateCategory}>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-new-category-name">
                        Nome
                      </label>
                      <input
                        id="finance-new-category-name"
                        name="name"
                        value={categoryFormState.name}
                        onChange={handleCategoryFormChange}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                        placeholder="Ex: Receitas"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="space-y-1">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-new-category-color">
                          Cor (opcional)
                        </label>
                        <input
                          id="finance-new-category-color"
                          name="color"
                          value={categoryFormState.color}
                          onChange={handleCategoryFormChange}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                          placeholder="#16a34a"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-new-category-description">
                          Descrição (opcional)
                        </label>
                        <input
                          id="finance-new-category-description"
                          name="description"
                          value={categoryFormState.description}
                          onChange={handleCategoryFormChange}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                          placeholder="Resumo da categoria"
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={categorySubmitting}
                      className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-xs font-medium text-white shadow hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                    >
                      <FaSave className="h-3 w-3" />
                      {categorySubmitting ? 'Salvando…' : 'Adicionar categoria'}
                    </button>
                  </form>
                </div>
                {categoryError && (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
                    {categoryError}
                  </div>
                )}
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-100 text-left text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-3 py-2">Nome</th>
                        <th className="px-3 py-2">Cor</th>
                        <th className="px-3 py-2">Subcategorias</th>
                        <th className="px-3 py-2 text-right">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {categories.length === 0 ? (
                        <tr>
                          <td className="px-3 py-3 text-sm text-slate-500" colSpan={4}>
                            Nenhuma categoria cadastrada.
                          </td>
                        </tr>
                      ) : (
                        categories.map((category) => {
                          const subcategoryCount = Array.isArray(category.subcategories)
                            ? category.subcategories.length
                            : 0;
                          return (
                            <tr key={category.id} className="text-sm text-slate-600">
                              <td className="px-3 py-3 font-medium text-slate-700">{category.name}</td>
                              <td className="px-3 py-3">
                                {category.color ? (
                                  <span className="inline-flex items-center gap-2 text-xs">
                                    <span
                                      className="inline-block h-3 w-3 rounded-full"
                                      style={{ backgroundColor: category.color }}
                                    />
                                    <span className="font-mono text-slate-500">{category.color}</span>
                                  </span>
                                ) : (
                                  <span className="text-xs text-slate-400">Sem cor</span>
                                )}
                              </td>
                              <td className="px-3 py-3 text-sm text-slate-500">
                                {subcategoryCount > 0
                                  ? `${subcategoryCount} subcategoria${subcategoryCount > 1 ? 's' : ''}`
                                  : 'Nenhuma subcategoria'}
                              </td>
                              <td className="px-3 py-3 text-right">
                                <div className="flex justify-end gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handleEditCategory(category)}
                                    disabled={
                                      categoryUpdatingId === category.id ||
                                      categoryDeletingId === category.id
                                    }
                                    className="inline-flex items-center gap-1 rounded-full border border-emerald-200 px-3 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:opacity-50"
                                  >
                                    <FaEdit className="h-3 w-3" />
                                    Editar
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteCategory(category.id)}
                                    disabled={categoryDeletingId === category.id}
                                    className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-3 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-200 disabled:opacity-50"
                                  >
                                    <FaTrash className="h-3 w-3" />
                                    {categoryDeletingId === category.id ? 'Removendo…' : 'Excluir'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <FaSitemap className="h-4 w-4 text-emerald-600" />
                    Nova subcategoria
                  </h4>
                  <form className="mt-3 space-y-3" onSubmit={handleCreateSubcategory}>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-new-subcategory-category">
                        Categoria
                      </label>
                      <select
                        id="finance-new-subcategory-category"
                        name="categoryId"
                        value={subcategoryFormState.categoryId}
                        onChange={handleSubcategoryFormChange}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                        required
                      >
                        <option value="">Selecione…</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-new-subcategory-name">
                        Nome
                      </label>
                      <input
                        id="finance-new-subcategory-name"
                        name="name"
                        value={subcategoryFormState.name}
                        onChange={handleSubcategoryFormChange}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                        placeholder="Ex: Receitas recorrentes"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div className="space-y-1">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-new-subcategory-color">
                          Cor (opcional)
                        </label>
                        <input
                          id="finance-new-subcategory-color"
                          name="color"
                          value={subcategoryFormState.color}
                          onChange={handleSubcategoryFormChange}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                          placeholder="#0ea5e9"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="finance-new-subcategory-description">
                          Descrição (opcional)
                        </label>
                        <input
                          id="finance-new-subcategory-description"
                          name="description"
                          value={subcategoryFormState.description}
                          onChange={handleSubcategoryFormChange}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200"
                          placeholder="Detalhes da subcategoria"
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={subcategorySubmitting || categories.length === 0}
                      className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-xs font-medium text-white shadow hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                    >
                      <FaSave className="h-3 w-3" />
                      {subcategorySubmitting ? 'Salvando…' : 'Adicionar subcategoria'}
                    </button>
                  </form>
                </div>
                {subcategoryError && (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700">
                    {subcategoryError}
                  </div>
                )}
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-100 text-left text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-3 py-2">Subcategoria</th>
                        <th className="px-3 py-2">Categoria</th>
                        <th className="px-3 py-2">Cor</th>
                        <th className="px-3 py-2 text-right">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {flattenedSubcategories.length === 0 ? (
                        <tr>
                          <td className="px-3 py-3 text-sm text-slate-500" colSpan={4}>
                            Nenhuma subcategoria cadastrada.
                          </td>
                        </tr>
                      ) : (
                        flattenedSubcategories.map((subcategory) => (
                          <tr key={subcategory.id} className="text-sm text-slate-600">
                            <td className="px-3 py-3 font-medium text-slate-700">{subcategory.name}</td>
                            <td className="px-3 py-3 text-sm text-slate-500">{subcategory.categoryName}</td>
                            <td className="px-3 py-3">
                              {subcategory.color ? (
                                <span className="inline-flex items-center gap-2 text-xs">
                                  <span
                                    className="inline-block h-3 w-3 rounded-full"
                                    style={{ backgroundColor: subcategory.color }}
                                  />
                                  <span className="font-mono text-slate-500">{subcategory.color}</span>
                                </span>
                              ) : (
                                <span className="text-xs text-slate-400">Sem cor</span>
                              )}
                            </td>
                            <td className="px-3 py-3 text-right">
                              <div className="flex justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleEditSubcategory(subcategory)}
                                  disabled={
                                    subcategoryUpdatingId === subcategory.id ||
                                    subcategoryDeletingId === subcategory.id
                                  }
                                  className="inline-flex items-center gap-1 rounded-full border border-emerald-200 px-3 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:opacity-50"
                                >
                                  <FaEdit className="h-3 w-3" />
                                  Editar
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteSubcategory(subcategory.id)}
                                  disabled={subcategoryDeletingId === subcategory.id}
                                  className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-3 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-200 disabled:opacity-50"
                                >
                                  <FaTrash className="h-3 w-3" />
                                  {subcategoryDeletingId === subcategory.id ? 'Removendo…' : 'Excluir'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCategoryReport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-6">
          <div className="relative max-h-[90vh] w-full max-w-6xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-slate-900">Resumo por categoria</h3>
                {categoryReportGeneratedAt && (
                  <p className="mt-1 text-xs text-slate-500">Gerado em {categoryReportGeneratedAt}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                {categoryReportLoading && (
                  <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                    <FaSync className="h-3 w-3 animate-spin" /> Carregando…
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleCloseCategoryReport}
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300"
                >
                  Fechar
                </button>
              </div>
            </div>

            <div className="mt-4 space-y-6">
              {categoryReportError ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                  {categoryReportError}
                </div>
              ) : categoryReportLoading && !hasCategoryReportData ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                  Carregando resumo financeiro…
                </div>
              ) : hasCategoryReportData ? (
                <>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">
                        Total de receitas
                      </p>
                      <p className="mt-2 text-lg font-semibold text-emerald-700">
                        {formatCurrency(categoryReportTotals.credits)}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-rose-100 bg-rose-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-rose-600">
                        Total de despesas
                      </p>
                      <p className="mt-2 text-lg font-semibold text-rose-600">
                        {formatCurrency(categoryReportTotals.debits)}
                      </p>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                        Saldo do período
                      </p>
                      <p
                        className={`mt-2 text-lg font-semibold ${
                          categoryReportTotals.balance >= 0
                            ? 'text-emerald-700'
                            : 'text-rose-600'
                        }`}
                      >
                        {formatCurrency(categoryReportTotals.balance)}
                      </p>
                    </div>
                  </div>

                  {(categoryMovementChart || categoryBalanceChart) && (
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                      {categoryMovementChart && (
                        <div className="rounded-2xl border border-slate-200 p-4">
                          <h4 className="text-sm font-semibold text-slate-700">
                            Distribuição das movimentações
                          </h4>
                          <div className="mt-4 h-64">
                            <Doughnut data={categoryMovementChart} options={categoryMovementChartOptions} />
                          </div>
                        </div>
                      )}
                      {categoryBalanceChart && (
                        <div className="rounded-2xl border border-slate-200 p-4">
                          <h4 className="text-sm font-semibold text-slate-700">
                            Receitas x despesas por categoria
                          </h4>
                          <div className="mt-4 h-64">
                            <Bar data={categoryBalanceChart} options={categoryBalanceChartOptions} />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {topSubcategoryChart && (
                    <div className="rounded-2xl border border-slate-200 p-4">
                      <h4 className="text-sm font-semibold text-slate-700">
                        Subcategorias com maior movimentação
                      </h4>
                      <div className="mt-4 h-72">
                        <Bar data={topSubcategoryChart} options={topSubcategoryChartOptions} />
                      </div>
                    </div>
                  )}

                  <div className="overflow-x-auto rounded-2xl border border-slate-200">
                    <table className="min-w-full divide-y divide-slate-100 text-left text-sm">
                      <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-4 py-3">Categoria / Subcategoria</th>
                          <th className="px-4 py-3 text-right">Transações</th>
                          <th className="px-4 py-3 text-right">Receitas</th>
                          <th className="px-4 py-3 text-right">Despesas</th>
                          <th className="px-4 py-3 text-right">Saldo</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {categoryReportData.categories.map((category) => {
                          const categoryKey = `category-${category.id ?? 'none'}-${category.name}`;
                          const subcategories = Array.isArray(category.subcategories)
                            ? category.subcategories
                            : [];
                          return (
                            <React.Fragment key={categoryKey}>
                              <tr className="bg-slate-50/60 text-sm text-slate-700">
                                <td className="px-4 py-3 font-semibold">
                                  <span className="inline-flex items-center gap-2">
                                    {category.color && (
                                      <span
                                        className="inline-block h-3 w-3 rounded-full"
                                        style={{ backgroundColor: category.color }}
                                      />
                                    )}
                                    {category.name}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right font-medium">
                                  {category.transactionCount}
                                </td>
                                <td className="px-4 py-3 text-right text-emerald-700">
                                  {formatCurrency(category.totalCredits)}
                                </td>
                                <td className="px-4 py-3 text-right text-rose-600">
                                  {formatCurrency(category.totalDebits)}
                                </td>
                                <td
                                  className={`px-4 py-3 text-right font-semibold ${
                                    category.balance >= 0 ? 'text-emerald-700' : 'text-rose-600'
                                  }`}
                                >
                                  {formatCurrency(category.balance)}
                                </td>
                              </tr>
                              {subcategories.length === 0 ? (
                                <tr key={`${categoryKey}-empty`} className="text-xs text-slate-500">
                                  <td className="px-4 py-3 pl-10" colSpan={5}>
                                    Sem subcategorias cadastradas para esta categoria.
                                  </td>
                                </tr>
                              ) : (
                                subcategories.map((subcategory) => {
                                  const subKey = `subcategory-${category.id ?? 'none'}-${
                                    subcategory.id ?? 'none'
                                  }`;
                                  return (
                                    <tr key={subKey} className="text-xs text-slate-600">
                                      <td className="px-4 py-2 pl-10">
                                        <span className="inline-flex items-center gap-2">
                                          {subcategory.color && (
                                            <span
                                              className="inline-block h-2.5 w-2.5 rounded-full"
                                              style={{ backgroundColor: subcategory.color }}
                                            />
                                          )}
                                          {subcategory.name}
                                        </span>
                                      </td>
                                      <td className="px-4 py-2 text-right">{subcategory.transactionCount}</td>
                                      <td className="px-4 py-2 text-right text-emerald-700">
                                        {formatCurrency(subcategory.totalCredits)}
                                      </td>
                                      <td className="px-4 py-2 text-right text-rose-600">
                                        {formatCurrency(subcategory.totalDebits)}
                                      </td>
                                      <td
                                        className={`px-4 py-2 text-right font-semibold ${
                                          subcategory.balance >= 0
                                            ? 'text-emerald-700'
                                            : 'text-rose-600'
                                        }`}
                                      >
                                        {formatCurrency(subcategory.balance)}
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                  Nenhuma movimentação encontrada para gerar o relatório.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ExpensesManager canManage={canManage} />
    </div>
  );
}
